import type { StorageProbe } from '../types';
import { log } from '../app/logger';
import { errorMessage } from '../app/errors';

/**
 * BAK-3 — "Request `navigator.storage.persist()` at install; display persistence status and
 * `storage.estimate()` in debug."
 *
 * U-0 owns the probe half of BAK-3. It matters here for two reasons beyond ticking the
 * requirement:
 *
 *  1. **A-4** assumes `persist()` is granted for an installed home-screen PWA. If it is not, the
 *     eviction risk the design accepts is larger than the design thinks, and BAK-1 export plus
 *     SYN-1 push carry more weight than planned. The harness is the first thing that can check.
 *  2. **NFR-4** budgets a 3-match day at ≤ 100 MB. `estimate()` is the only instrument for that,
 *     and Safari's quota is a fraction of free disk rather than a fixed number — so it has to be
 *     read on the actual phone, with the actual amount of free space that phone has.
 *
 * Nothing here throws. A probe failure produces a `StorageProbe` with `error` populated and a
 * logged warning — the run is still worth taking, and losing the latency measurement because a
 * storage API was unavailable would be the wrong trade. That is a *reported* failure, not a
 * swallowed one (NFR-8).
 */

const MODULE = 'storageProbe';

function unsupported(reason: string): StorageProbe {
  return {
    supported: false,
    usageBytes: null,
    quotaBytes: null,
    usageFraction: null,
    persistedBefore: null,
    persistRequested: null,
    persistedAfter: null,
    breakdown: null,
    error: reason,
  };
}

/**
 * @param requestPersist when true, calls `navigator.storage.persist()` (BAK-3's "at install").
 *   The harness offers it as an explicit control rather than doing it silently, because on iOS the
 *   grant decision is one-way and Andrew should know when it was asked for.
 */
export async function probeStorage(requestPersist: boolean): Promise<StorageProbe> {
  const storage = navigator.storage as StorageManager | undefined;

  if (!storage || typeof storage.estimate !== 'function') {
    const reason = 'navigator.storage.estimate() is unavailable in this browser.';
    log.warn(MODULE, reason);
    return unsupported(reason);
  }

  let persistedBefore: boolean | null = null;
  try {
    persistedBefore = typeof storage.persisted === 'function' ? await storage.persisted() : null;
  } catch (cause) {
    log.exception(MODULE, 'navigator.storage.persisted() threw', cause);
    persistedBefore = null;
  }

  let persistRequested: boolean | null = null;
  let persistedAfter: boolean | null = persistedBefore;
  if (requestPersist && typeof storage.persist === 'function') {
    try {
      persistRequested = await storage.persist();
      persistedAfter = persistRequested;
      log.info(MODULE, 'BAK-3: navigator.storage.persist() requested', {
        granted: persistRequested,
      });
    } catch (cause) {
      log.exception(MODULE, 'navigator.storage.persist() threw', cause);
      persistRequested = null;
    }
  }

  try {
    const estimate = await storage.estimate();
    const usageBytes = typeof estimate.usage === 'number' ? estimate.usage : null;
    const quotaBytes = typeof estimate.quota === 'number' ? estimate.quota : null;

    // Non-standard; Chrome provides it, Safari currently does not. Recorded verbatim when present
    // rather than reshaped, so a future Safari that adds it needs no code change to be captured.
    const raw = estimate as StorageEstimate & { usageDetails?: Record<string, number> };
    const breakdown = raw.usageDetails ?? null;

    return {
      supported: true,
      usageBytes,
      quotaBytes,
      usageFraction:
        usageBytes !== null && quotaBytes !== null && quotaBytes > 0 ? usageBytes / quotaBytes : null,
      persistedBefore,
      persistRequested,
      persistedAfter,
      breakdown,
      error: null,
    };
  } catch (cause) {
    const reason = `navigator.storage.estimate() failed: ${errorMessage(cause)}`;
    log.exception(MODULE, 'BAK-3 storage estimate failed', cause);
    return { ...unsupported(reason), persistedBefore, persistRequested, persistedAfter };
  }
}

/** Human-readable byte size for the results screen. Binary units, one decimal. */
export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(1)} ${units[unit]}`;
}
